import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Stack, Group, Card, Table, Text, Button, Modal, TextInput, ColorInput,
  Center, Loader, Alert, Box, ActionIcon, Badge,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Plus, Pencil, Trash2, Check, Layers, ChevronRight } from 'lucide-react';

import { groupsApi, membersApi, type Group as GroupType } from '../api';
import { numStyle } from '../theme';
import { plural } from '../utils/format';

/** Přednastavené barvy si přenášíme z původního webu, ať se skupiny nepřebarví. */
const SWATCHES = ['#2196F3', '#4CAF50', '#F44336', '#FFEB3B', '#000000', '#FF9800', '#9C27B0', '#00BCD4'];

export default function GroupsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 48em)');

  const [editing, setEditing] = useState<GroupType | null>(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState(SWATCHES[0]);
  const [nameError, setNameError] = useState<string | null>(null);

  const groups = useQuery({ queryKey: ['groups'], queryFn: groupsApi.getAll });
  const members = useQuery({ queryKey: ['members'], queryFn: () => membersApi.getAll() });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['groups'] });
    qc.invalidateQueries({ queryKey: ['members'] });
  };

  const save = useMutation({
    mutationFn: () =>
      editing
        ? groupsApi.update(editing.id, { name: name.trim(), color })
        : groupsApi.create({ name: name.trim(), color }),
    onSuccess: () => {
      notifications.show({ message: editing ? 'Skupina uložena' : 'Skupina přidána', color: 'forest' });
      setOpen(false);
      invalidate();
    },
    onError: (e: any) =>
      notifications.show({ title: 'Uložení se nepovedlo', message: e?.response?.data?.error || e.message, color: 'red' }),
  });

  const remove = useMutation({
    mutationFn: groupsApi.delete,
    onSuccess: () => { notifications.show({ message: 'Skupina smazána' }); invalidate(); },
    onError: (e: any) =>
      notifications.show({ title: 'Smazání se nepovedlo', message: e?.response?.data?.error || e.message, color: 'red' }),
  });

  const openNew = () => { setEditing(null); setName(''); setColor(SWATCHES[0]); setNameError(null); setOpen(true); };
  const openEdit = (g: GroupType) => {
    setEditing(g); setName(g.name); setColor(g.color || SWATCHES[0]); setNameError(null); setOpen(true);
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) { setNameError('Doplň název'); return; }
    save.mutate();
  };

  const countIn = (id: number) => (members.data ?? []).filter((m) => m.group_id === id).length;

  const confirmDelete = (g: GroupType) => {
    const n = countIn(g.id);
    const warn = n > 0
      ? `Skupina ${g.name} má ${plural(n, 'člena', 'členy', 'členů')}. Opravdu smazat?`
      : `Opravdu smazat skupinu ${g.name}?`;
    if (window.confirm(warn)) remove.mutate(g.id);
  };

  if (groups.isLoading) return <Center h={240}><Loader /></Center>;
  if (groups.error) {
    return <Alert color="red" title="Skupiny se nepodařilo načíst">{(groups.error as any)?.message}</Alert>;
  }

  const list = groups.data ?? [];

  const swatch = (g: GroupType, size = 11) => (
    <span
      style={{
        width: size, height: size, borderRadius: 2, flex: `0 0 ${size}px`,
        background: g.color || 'var(--mantine-color-gray-5)',
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.12)',
      }}
    />
  );

  const rowActions = (g: GroupType) => (
    <Group gap={2} justify="flex-end" wrap="nowrap">
      <ActionIcon aria-label={`Upravit ${g.name}`} onClick={() => openEdit(g)}>
        <Pencil size={14} />
      </ActionIcon>
      <ActionIcon aria-label={`Smazat ${g.name}`} color="red" onClick={() => confirmDelete(g)}>
        <Trash2 size={14} />
      </ActionIcon>
    </Group>
  );

  return (
    <Stack gap="sm">
      <Group justify="flex-end">
        <Button leftSection={<Plus size={14} />} onClick={openNew}>Přidat skupinu</Button>
      </Group>

      <Card padding={0}>
        <Group p="sm" justify="space-between" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
          <Text fw={700} fz="sm">{plural(list.length, 'skupina', 'skupiny', 'skupin')}</Text>
          <Text c="dimmed" fz="sm">{plural((members.data ?? []).length, 'člen', 'členové', 'členů')} celkem</Text>
        </Group>

        {list.length === 0 ? (
          <Stack align="center" py="xl" gap={4}>
            <Layers size={22} color="var(--mantine-color-dimmed)" />
            <Text fw={600}>Zatím tu není žádná skupina</Text>
            <Text c="dimmed" fz="sm">Bez skupin se nedá zakládat členy ani počítat body.</Text>
          </Stack>
        ) : isMobile ? (
          <Box>
            {list.map((g) => (
              <Group key={g.id} p="sm" gap="sm" wrap="nowrap"
                style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                {swatch(g, 13)}
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={650} fz="sm" truncate>{g.name}</Text>
                  <Text c="dimmed" fz="xs" style={numStyle}>
                    {plural(countIn(g.id), 'člen', 'členové', 'členů')} · {g.color}
                  </Text>
                </Box>
                {rowActions(g)}
              </Group>
            ))}
          </Box>
        ) : (
          <Table.ScrollContainer minWidth={520}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Skupina</Table.Th>
                  <Table.Th w={130} ta="right">Členů</Table.Th>
                  <Table.Th w={120}>Barva</Table.Th>
                  <Table.Th w={80} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.map((g) => (
                  <Table.Tr key={g.id}>
                    <Table.Td>
                      <Group gap={8} wrap="nowrap">
                        {swatch(g)}
                        <Text fw={600} fz="sm">{g.name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Button
                        size="compact-xs" variant="subtle" color="gray"
                        rightSection={<ChevronRight size={12} />}
                        onClick={() => navigate(`/clenove?skupina=${g.id}`)}
                      >
                        <Text component="span" style={numStyle} fz="sm">{countIn(g.id)}</Text>
                      </Button>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color="gray" style={numStyle} tt="uppercase">{g.color}</Badge>
                    </Table.Td>
                    <Table.Td>{rowActions(g)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      <Modal opened={open} onClose={() => setOpen(false)} title={editing ? 'Upravit skupinu' : 'Nová skupina'}>
        <form onSubmit={submit}>
          <Stack gap="sm">
            <TextInput
              label="Název" placeholder="např. Rysi" value={name} error={nameError}
              onChange={(e) => { setName(e.currentTarget.value); setNameError(null); }}
              data-autofocus
            />
            <ColorInput
              label="Barva"
              description="Barvou se skupina pozná v žebříčcích i na stanovištích."
              value={color} onChange={setColor} swatches={SWATCHES} format="hex"
            />
            <Group justify="flex-end" mt="xs">
              <Button variant="default" type="button" onClick={() => setOpen(false)}>Zrušit</Button>
              <Button type="submit" leftSection={<Check size={14} />} loading={save.isPending}>
                {editing ? 'Uložit změny' : 'Přidat skupinu'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
